import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { usePageTitle } from "../lib/usePageTitle";
import { KeyRound, Search, Lock, ArrowRight, ArrowLeft, ShieldQuestion, CheckCircle2 } from "lucide-react";

export default function ForgotPasswordPage() {
  usePageTitle("Mot de passe oublié");
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [query, setQuery] = useState("");
  const [token, setToken] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const navigate = useNavigate();

  const startStep = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot/start", { email: email.trim() });
      setHasHistory(!!data.has_history);
      setStep(2);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const verifyStep = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot/verify", { email: email.trim(), query: query.trim() });
      setToken(data.reset_token);
      setStep(3);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Réponse incorrecte");
    } finally { setLoading(false); }
  };

  const resetStep = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPwd.length < 6) { setError("Mot de passe trop court (min 6 caractères)"); return; }
    if (newPwd !== confirmPwd) { setError("Les mots de passe ne correspondent pas"); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot/reset", { token, new_password: newPwd });
      setStep(4);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-[var(--omni-surface)]">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo size="lg" /></div>
        <div className="bg-white border border-[var(--omni-border)] rounded-2xl p-8" data-testid="forgot-card">
          {/* Stepper */}
          <div className="flex items-center justify-between mb-6">
            {[1, 2, 3].map((n) => (
              <div key={n} className="flex-1 flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition ${step >= n ? "bg-[var(--omni-accent)] text-white" : "bg-gray-200 text-gray-500"}`}
                  data-testid={`forgot-step-${n}`}
                >
                  {step > n ? <CheckCircle2 size={16} /> : n}
                </div>
                {n < 3 && <div className={`flex-1 h-0.5 mx-1 ${step > n ? "bg-[var(--omni-accent)]" : "bg-gray-200"}`} />}
              </div>
            ))}
          </div>

          {step === 1 && (
            <form onSubmit={startStep} data-testid="forgot-step-1-form">
              <div className="flex items-center gap-2 mb-1">
                <KeyRound size={18} className="text-[var(--omni-accent)]" />
                <h1 className="text-2xl font-bold">Mot de passe oublié</h1>
              </div>
              <p className="text-sm text-gray-500 mb-6">Entrez l'adresse de votre compte OmniSearch pour démarrer la récupération.</p>
              <Label htmlFor="forgot-email" className="text-xs">Email</Label>
              <Input id="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="forgot-email-input" />
              {error && <div className="text-sm text-red-600 mt-3" data-testid="forgot-error">{error}</div>}
              <Button type="submit" disabled={loading || !email} className="w-full mt-5 bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white rounded-full" data-testid="forgot-step-1-submit">
                {loading ? "Vérification…" : (<>Continuer <ArrowRight size={15} className="ml-1" /></>)}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verifyStep} data-testid="forgot-step-2-form">
              <div className="flex items-center gap-2 mb-1">
                <ShieldQuestion size={18} className="text-[var(--omni-accent)]" />
                <h1 className="text-2xl font-bold">Question de sécurité</h1>
              </div>
              {!hasHistory ? (
                <>
                  <p className="text-sm text-gray-500 mb-2">Ce compte ne dispose pas encore d'historique de recherche suffisant pour cette procédure.</p>
                  <p className="text-sm text-gray-500 mb-6">Contactez le support à <a href="mailto:support@omnisearch.io" className="text-[var(--omni-accent)] hover:underline">support@omnisearch.io</a>.</p>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-500 mb-6">
                    Pour prouver que c'est bien votre compte, tapez <strong>une recherche</strong> que vous avez effectuée récemment sur OmniSearch (parmi vos 20 dernières).
                  </p>
                  <Label htmlFor="forgot-query" className="text-xs">Une de vos recherches récentes</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={15} />
                    <Input id="forgot-query" required value={query} onChange={(e) => setQuery(e.target.value)} placeholder="ex: recettes pâtes" className="pl-9" data-testid="forgot-query-input" />
                  </div>
                  {error && <div className="text-sm text-red-600 mt-3" data-testid="forgot-error">{error}</div>}
                </>
              )}
              <div className="flex gap-2 mt-5">
                <Button type="button" variant="outline" onClick={() => { setStep(1); setError(null); }} className="rounded-full" data-testid="forgot-step-2-back">
                  <ArrowLeft size={15} className="mr-1" /> Retour
                </Button>
                {hasHistory && (
                  <Button type="submit" disabled={loading || !query.trim()} className="flex-1 bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white rounded-full" data-testid="forgot-step-2-submit">
                    {loading ? "Vérification…" : "Valider"}
                  </Button>
                )}
              </div>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={resetStep} data-testid="forgot-step-3-form">
              <div className="flex items-center gap-2 mb-1">
                <Lock size={18} className="text-[var(--omni-accent)]" />
                <h1 className="text-2xl font-bold">Nouveau mot de passe</h1>
              </div>
              <p className="text-sm text-gray-500 mb-6">Choisissez un nouveau mot de passe (minimum 6 caractères).</p>
              <Label htmlFor="forgot-newpwd" className="text-xs">Nouveau mot de passe</Label>
              <Input id="forgot-newpwd" type="password" required minLength={6} value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="mt-1" data-testid="forgot-newpwd-input" />
              <Label htmlFor="forgot-confirm" className="text-xs mt-3 inline-block">Confirmation</Label>
              <Input id="forgot-confirm" type="password" required minLength={6} value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className="mt-1" data-testid="forgot-confirm-input" />
              {error && <div className="text-sm text-red-600 mt-3" data-testid="forgot-error">{error}</div>}
              <Button type="submit" disabled={loading} className="w-full mt-5 bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white rounded-full" data-testid="forgot-step-3-submit">
                {loading ? "Mise à jour…" : "Définir le nouveau mot de passe"}
              </Button>
            </form>
          )}

          {step === 4 && (
            <div className="text-center py-4" data-testid="forgot-success">
              <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-3" />
              <h1 className="text-2xl font-bold mb-2">Mot de passe mis à jour</h1>
              <p className="text-sm text-gray-500 mb-6">Vous pouvez maintenant vous connecter.</p>
              <Button onClick={() => navigate("/login")} className="bg-black hover:bg-gray-800 text-white rounded-full px-8" data-testid="forgot-back-to-login">
                Aller à la connexion
              </Button>
            </div>
          )}

          {step !== 4 && (
            <div className="text-center text-sm text-gray-500 mt-6">
              <Link to="/login" className="hover:text-black" data-testid="forgot-cancel-link">Annuler et revenir à la connexion</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
